// covered-by: frontends/carrie/src/routes/ask-puntt/hooks/__tests__/useUiPrefs.test.ts
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "askpuntt-ui-prefs";
/** Same-tab broadcast: the `storage` event only reaches other tabs. */
const SAME_TAB_EVENT = "askpuntt-ui-prefs-change";

/**
 * AskPuntt UI preference blob persisted in localStorage. Single key so we
 * don't sprinkle dozens of `askpuntt-*-seen` keys across the route. Each
 * field is optional and defaults to false on the read path.
 */
export type UiPrefs = {
  /** Opt-in native notifications when background chats become unread. */
  askPunttDesktopNotifications?: boolean;
  /** Set true after the resize-hint tooltip has been shown once. */
  resizeHintSeen?: boolean;
  /** Artifact sidebar section expand/collapse preferences. */
  artifactRail?: {
    reviewsOpen?: boolean;
    documentsOpen?: boolean;
    detailsOpen?: boolean;
  };
  /**
   * Legacy Standard/Deep composer preference. No longer written — Deep is
   * launched from the Deep Artwork Review workflow card. Kept optional so old
   * localStorage blobs still parse.
   */
  artworkReviewMode?: "standard" | "deep";
  /**
   * One-shot Try walkthroughs that have been consumed after a successful
   * Skip / Get started / card-body launch. Keyed by try feature id
   * (e.g. `artwork_review`).
   */
  seenTryFeatures?: Record<string, boolean>;
  /** Per-chat dismiss for the review-complete email notification CTA. */
  dismissedReviewEmailNotificationCtaByChat?: Record<string, true>;
};

function safeRead(): UiPrefs {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      return parsed as UiPrefs;
    }
    return {};
  } catch {
    return {};
  }
}

function safeWrite(prefs: UiPrefs): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Quota / disabled — drop silently. In-session state still holds.
  }
}

export type UseUiPrefsResult = {
  prefs: UiPrefs;
  setPref: <K extends keyof UiPrefs>(key: K, value: UiPrefs[K]) => void;
};

/**
 * Read + write the single askpuntt-ui-prefs JSON blob in localStorage.
 *
 * Designed so adding a new one-shot UI flag (e.g., "new-feature-coachmark
 * seen") doesn't add another storage key — just another field on
 * `UiPrefs`. Reads are synchronous-on-mount; writes flush immediately
 * (no debounce — these are rare, terminal events).
 */
export function useUiPrefs(): UseUiPrefsResult {
  const [prefs, setPrefs] = useState<UiPrefs>(() => safeRead());

  // If another tab, or another useUiPrefs instance in this tab, updates the
  // blob, mirror it locally so a seen hint doesn't pop up again.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      setPrefs(safeRead());
    };
    const onSameTab = () => setPrefs((prev) => ({ ...prev, ...safeRead() }));
    window.addEventListener("storage", onStorage);
    window.addEventListener(SAME_TAB_EVENT, onSameTab);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(SAME_TAB_EVENT, onSameTab);
    };
  }, []);

  const setPref = useCallback(
    <K extends keyof UiPrefs>(key: K, value: UiPrefs[K]) => {
      // why: merge onto the stored blob, not this instance's snapshot, or a
      // write here erases keys another mounted instance saved since mount.
      const next = { ...safeRead(), [key]: value };
      safeWrite(next);
      setPrefs((prev) => ({ ...prev, ...next }));
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event(SAME_TAB_EVENT));
      }
    },
    [],
  );

  return { prefs, setPref };
}
